<div class="adf-page" [class.is-readonly]="readonly">
  <div class="adf-hero">
    <p class="adf-hero-text">
      Configure ageing calculations and business rules for action management. Set response age limits, business hours, and rule engine frequency.
    </p>
  </div>

  <div class="adf-loader" *ngIf="!configurationLoaded">
    <app-ltms-loader></app-ltms-loader>
  </div>

  <form [formGroup]="ageingForm" class="adf-form" *ngIf="configurationLoaded">
    <div class="adf-groups-container">
      <section class="adf-group">
        <div class="adf-group-header">
          <h5 class="adf-group-title">Ageing Calculations</h5>
          <p class="adf-group-desc">Set closed status, rule frequency, and response age limits</p>
        </div>
        <div class="adf-group-body">
          <div class="adf-setting-row">
            <div class="adf-setting-copy">
              <label class="adf-setting-label" for="closed-status">
                Status to be considered as Closed
              </label>
              <p class="adf-setting-desc">
                Define which status values should be treated as closed actions
              </p>
            </div>
            <div class="adf-setting-control">
              <div class="adf-multiselect" [class.is-open]="showStatusDropdown" [class.is-disabled]="readonly">
                <div class="adf-multiselect-trigger" id="closed-status" (click)="toggleStatusDropdown($event)">
                  <div class="adf-multiselect-tags" *ngIf="selectedClosedStatuses.length > 0">
                    <span *ngFor="let status of selectedClosedStatuses" class="adf-tag">
                      {{ status }}
                      <i class="fa fa-times adf-tag-remove" *ngIf="!readonly"
                        (click)="onStatusRemove(status); $event.stopPropagation()"></i>
                    </span>
                  </div>
                  <span class="adf-multiselect-placeholder" *ngIf="selectedClosedStatuses.length === 0">
                    Select status...
                  </span>
                  <i class="fa fa-chevron-down adf-multiselect-arrow"
                    [class.is-rotated]="showStatusDropdown"></i>
                </div>

                <div class="adf-multiselect-dropdown" *ngIf="showStatusDropdown">
                  <div *ngFor="let status of getAvailableStatuses()" class="adf-multiselect-option"
                    [class.is-selected]="isStatusSelected(status)" (click)="onStatusSelect(status)">
                    <i class="fa fa-check" *ngIf="isStatusSelected(status)"></i>
                    {{ status.name || status }}
                  </div>
                  <div class="adf-multiselect-option is-disabled" *ngIf="getAvailableStatuses().length === 0">
                    No statuses available
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="adf-setting-row">
            <div class="adf-setting-copy">
              <label class="adf-setting-label" for="rule-frequency">
                Rule Engine Run Frequency (in Minutes)
              </label>
              <p class="adf-setting-desc">
                How often the rule engine should process actions
              </p>
            </div>
            <div class="adf-setting-control">
              <input type="number" class="adf-input adf-input--sm" id="rule-frequency"
                formControlName="ruleEngineFrequency"
                (input)="onInputChange('ruleEngineFrequency', $any($event.target).value)"
                min="1" max="1440" placeholder="60">
            </div>
          </div>

          <div class="adf-setting-row">
            <div class="adf-setting-copy">
              <label class="adf-setting-label" for="max-age">
                Maximum Age of Response to be considered (in Days)
              </label>
              <p class="adf-setting-desc">
                Actions older than this will not be processed by the rule engine
              </p>
            </div>
            <div class="adf-setting-control">
              <input type="number" class="adf-input adf-input--sm" id="max-age"
                formControlName="maxResponseAge"
                (input)="onInputChange('maxResponseAge', $any($event.target).value)"
                min="1" max="365" placeholder="15">
            </div>
          </div>

          <div class="adf-setting-row">
            <div class="adf-setting-copy">
              <label class="adf-setting-label" for="non-business-hours">
                Don't Run in Non Business Hours
              </label>
              <p class="adf-setting-desc">
                Pause action processing outside of business hours
              </p>
            </div>
            <div class="adf-setting-control">
              <label class="adf-switch">
                <input type="checkbox" id="non-business-hours"
                  formControlName="dontRunNonBusinessHours"
                  (change)="onInputChange('dontRunNonBusinessHours', $any($event.target).checked)">
                <span class="adf-switch-slider"></span>
              </label>
            </div>
          </div>
        </div>
        <div class="adf-group-footer">
          <button type="button" class="adf-btn adf-btn--ghost" (click)="cancelConfiguration('calculations')"
            [disabled]="!isCalculationsSaveEnabled || isCancellingSection === 'calculations' || readonly">Cancel</button>
          <button type="button" class="adf-btn adf-btn--primary" (click)="saveConfiguration('calculations')"
            [disabled]="!isCalculationsSaveEnabled || isSavingSection === 'calculations' || readonly">Save</button>
        </div>
      </section>

      <section class="adf-group">
        <div class="adf-group-header">
          <h5 class="adf-group-title">Business Calendar</h5>
          <p class="adf-group-desc">Define business hours, working days, and holidays</p>
        </div>
        <div class="adf-group-body">
          <div class="adf-setting-row adf-setting-row--stack">
            <div class="adf-setting-copy adf-setting-copy--wide">
              <span class="adf-setting-label">Business Timings</span>
              <p class="adf-setting-desc">
                Define when your business operates for action processing
              </p>
            </div>
            <div class="adf-setting-control adf-setting-control--wide">
              <div class="adf-time-pair">
                <div class="adf-time-field">
                  <label class="adf-field-label" for="start-time">Start Time</label>
                  <input type="time" class="adf-input" id="start-time" formControlName="businessStartTime"
                    (input)="onInputChange('businessStartTime', $any($event.target).value)">
                </div>
                <div class="adf-time-field">
                  <label class="adf-field-label" for="end-time">End Time</label>
                  <input type="time" class="adf-input" id="end-time" formControlName="businessEndTime"
                    (input)="onInputChange('businessEndTime', $any($event.target).value)">
                </div>
              </div>
            </div>
          </div>

          <div class="adf-setting-row adf-setting-row--stack">
            <div class="adf-setting-copy adf-setting-copy--wide">
              <span class="adf-setting-label">Business Days</span>
              <p class="adf-setting-desc">
                Select which days of the week are considered business days
              </p>
            </div>
            <div class="adf-setting-control adf-setting-control--wide">
              <div class="adf-day-chips">
                <button type="button" *ngFor="let day of businessDays" class="adf-day-chip"
                  [class.is-selected]="isBusinessDaySelected(day)"
                  [disabled]="readonly"
                  (click)="onBusinessDayToggle(day)">
                  {{ day }}
                </button>
              </div>
            </div>
          </div>

          <div class="adf-setting-row adf-setting-row--stack">
            <div class="adf-setting-copy adf-setting-copy--wide">
              <span class="adf-setting-label">Holiday List</span>
              <p class="adf-setting-desc">
                Specify holidays. Business age of responses will exclude these dates.
              </p>
            </div>
            <div class="adf-setting-control adf-setting-control--wide">
              <div class="adf-holiday" [class.is-disabled]="readonly">
                <div class="adf-holiday-toolbar">
                  <div class="adf-holiday-picker">
                    <label class="adf-field-label" for="holiday-date">Add holiday</label>
                    <div class="adf-holiday-picker-row">
                      <input type="date" class="adf-input adf-holiday-input" id="holiday-date"
                        [(ngModel)]="holidayPickerValue" [ngModelOptions]="{standalone: true}"
                        [disabled]="readonly" (keydown.enter)="addHolidayDate(); $event.preventDefault()">
                      <button type="button" class="adf-btn adf-btn--primary adf-holiday-add"
                        [disabled]="readonly || !holidayPickerValue" (click)="addHolidayDate()">
                        Add
                      </button>
                    </div>
                  </div>
                  <button type="button" class="adf-btn adf-btn--ghost adf-holiday-clear"
                    *ngIf="holidayDates.length > 0"
                    [disabled]="readonly" (click)="clearHolidayDates()">
                    Clear all
                  </button>
                </div>

                <div class="adf-holiday-chips" *ngIf="holidayDates.length > 0">
                  <span class="adf-tag adf-holiday-chip" *ngFor="let holiday of holidayDates">
                    {{ formatHolidayLabel(holiday) }}
                    <i class="fa fa-times adf-tag-remove" *ngIf="!readonly"
                      (click)="removeHolidayDate(holiday)"></i>
                  </span>
                </div>

                <div class="adf-holiday-empty" *ngIf="holidayDates.length === 0">
                  No holidays added. Pick a date and click Add.
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="adf-group-footer">
          <button type="button" class="adf-btn adf-btn--ghost" (click)="cancelConfiguration('calendar')"
            [disabled]="!isCalendarSaveEnabled || isCancellingSection === 'calendar' || readonly">Cancel</button>
          <button type="button" class="adf-btn adf-btn--primary" (click)="saveConfiguration('calendar')"
            [disabled]="!isCalendarSaveEnabled || isSavingSection === 'calendar' || readonly">Save</button>
        </div>
      </section>
    </div>
  </form>
</div>
